import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import styles from "./toolbox-backdrop.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import {
  Backdrop,
  BackdropStyle,
  BackdropType,
  TextAlignment,
} from "PosterEditor/model/poster-project";
import { ToolboxComponentColorPicker as ColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { pickerColors } from "PosterEditor/model/poster-project";
import {
  defaultBackdrop,
  defaultBackdropStyle,
  defaultLabelStyle,
} from "PosterEditor/model/default-project-style";
import { useState, useEffect } from "react";
import { ToolboxComponentInput } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { ToolboxComponentFontPicker as FontPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-font-picker";
import { fontNames } from "PosterEditor/model/poster-project";
import { Size } from "PosterEditor/model/poster-types";
import { ToolboxComponentDropdown as Dropdown } from "../../toolbox-components/toolbox-component-dropdown";
import {
  DeleteButton,
  BackButton,
  UIIconButton,
} from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import { DropdownMenu } from "components/common/dropdown-menu";
import { FileUploadButton } from "../route-source/toolbox-route-source";
import { set } from "lodash";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { SaveImageConfig } from "entity-store/image-store/use-image-store-remote";
import { v4 as uuid } from "uuid";

interface ToolboxBackdropProps {
  backdrop: Backdrop;
}

export const ToolboxBackdrop = (props: ToolboxBackdropProps) => {
  const {
    setBackdrop: saveBackdropToProject,
    getBackdrop,
    removeBackdrop,
    isTemplateEditing,
  } = usePosterProject();
  const { editorUIState, setEditingElement } = useEditorUI();

  const [backdrop, setBackdrop] = useState<Backdrop>(props.backdrop);

  useEffect(() => {
    const backdropID = props.backdrop.id;
    const backdrop = getBackdrop(backdropID) ?? defaultBackdrop;
    setBackdrop(backdrop);
  }, [
    getBackdrop,
    props.backdrop?.id,
    props.backdrop.position,
    props.backdrop.size,
  ]);

  const updateBackdrop = (style?: {
    backgroundColor?: string;
    foregroundColor?: string;
    backgroundOpacity?: number;
    foregroundOpacity?: number;
    cornerRadius?: number;
    borderWidth?: number;
    borderColor?: string;
    name?: string;
    type?: BackdropType;
    imageName?: string;
    padding?: number;
  }) => {
    if (!backdrop) {
      return;
    }
    const updatedBackdrop: Backdrop = {
      ...backdrop,
      name: style?.name ?? backdrop.name,
      // position: backdrop.position,
      type: style?.type ?? backdrop.type,
      style: {
        ...backdrop.style,
        backgroundColor:
          style?.backgroundColor ?? backdrop.style.backgroundColor,
        foregroundColor:
          style?.foregroundColor ?? backdrop.style.foregroundColor,
        opacity: style?.backgroundOpacity ?? backdrop.style.opacity,
        foregroundOpacity:
          style?.foregroundOpacity ?? backdrop.style.foregroundOpacity,
        cornerRadius: style?.cornerRadius ?? backdrop.style.cornerRadius,
        borderWidth: style?.borderWidth ?? backdrop.style.borderWidth,
        borderColor: style?.borderColor ?? backdrop.style.borderColor,
        imageName: style?.imageName ?? backdrop.style.imageName,
        padding: style?.padding ?? backdrop.style.padding,
      },
    };
    setBackdrop(updatedBackdrop);
    saveBackdropToProject(updatedBackdrop);
  };

  const shortestSide = () => {
    const mapSize = editorUIState.mapSizeInPX;
    if (!mapSize) {
      return 50;
    }
    const backdropSize: Size = {
      width: backdrop.size.width * mapSize.width,
      height: backdrop.size.height * mapSize.height,
    };
    const borderWidth = backdrop.style.borderWidth;
    return Math.min(
      backdropSize.width - borderWidth,
      backdropSize.height - borderWidth
    );
  };

  return (
    <div className={styles.container}>
      <Header
        backdrop={backdrop}
        onClickBackButton={() => setEditingElement(undefined)}
        onClickDelete={() => {
          removeBackdrop(props.backdrop);
          setEditingElement(undefined);
        }}
        onChangeBackdropName={(name) => {
          updateBackdrop({ name });
        }}
      />
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          {isTemplateEditing && (
            <>
              <BackdropTypeDropdown
                backdrop={backdrop}
                onChangeType={(type) => {
                  updateBackdrop({ type });
                }}
              />
              <Divider />
            </>
          )}
          {backdrop.type === "image" && (
            <>
              <ImageControl
                backdrop={backdrop}
                onChangeImage={(name) => {
                  updateBackdrop({ imageName: name });
                }}
              />
              <Divider />
            </>
          )}
          <BackgroundControl
            showForegroundControls={backdrop.type === "dataVisualization"}
            backgroundColor={backdrop?.style.backgroundColor ?? "white"}
            forgroundColor={backdrop?.style.foregroundColor ?? "black"}
            backgroundOpacity={
              backdrop?.style.opacity ?? defaultBackdropStyle.opacity ?? 0.5
            }
            foregroundOpacity={
              backdrop?.style.foregroundOpacity ??
              defaultBackdropStyle.foregroundOpacity ??
              1
            }
            onChangeBackgroundColor={(color) => {
              updateBackdrop({ backgroundColor: color });
            }}
            onChangeBackgroundOpacity={(opacity) => {
              updateBackdrop({ backgroundOpacity: opacity });
            }}
            onChangeForegroundColor={(color) => {
              updateBackdrop({ foregroundColor: color });
            }}
            onChangeForegroundOpacity={(opacity) => {
              updateBackdrop({ foregroundOpacity: opacity });
            }}
          />
          <Divider />
          {backdrop.type === "dataVisualization" && (
            <PaddingControl
              padding={backdrop.style.padding ?? defaultBackdropStyle.padding}
              onChangePadding={(padding) => {
                updateBackdrop({ padding });
              }}
            />
          )}
          <BorderControl
            color={backdrop?.style.borderColor ?? "white"}
            onChangeColor={(color) => {
              updateBackdrop({ borderColor: color });
            }}
            width={backdrop?.style.borderWidth ?? 2}
            onChangeWidth={(width) => {
              updateBackdrop({ borderWidth: width });
            }}
            cornerRadius={backdrop?.style.cornerRadius ?? 3}
            onChangeCornerRadius={(cornerRadius) => {
              updateBackdrop({ cornerRadius: cornerRadius });
            }}
            rectShortestSide={shortestSide()}
          />
        </div>
      </Toolbox>
    </div>
  );
};

interface HeaderProps {
  backdrop: Backdrop;
  onChangeBackdropName: (name: string) => void;
  onClickBackButton: () => void;
  onClickDelete: () => void;
}

const Header = (props: HeaderProps) => {
  return (
    <div className={styles.header}>
      <div className={styles.headerLeading}>
        <BackButton onClick={props.onClickBackButton} />
        <TextContentControl
          text={props.backdrop.name}
          onChangeText={props.onChangeBackdropName}
          onClickDeleteLabel={() => {}}
        />
      </div>
      <div className={styles.headerTrailing}>
        <DeleteButton onClick={props.onClickDelete} />
      </div>
    </div>
  );
};

interface BackgroundControlProps {
  showForegroundControls: boolean;
  backgroundColor: string;
  forgroundColor: string;
  backgroundOpacity: number;
  foregroundOpacity: number;
  onChangeBackgroundColor: (color: string) => void;
  onChangeForegroundColor: (color: string) => void;
  onChangeBackgroundOpacity: (opacity: number) => void;
  onChangeForegroundOpacity: (opacity: number) => void;
}

const BackgroundControl = (props: BackgroundControlProps) => {
  const [isBackgroundColorExpanded, setIsBackgroundColorExpanded] =
    useState(false);
  const [isForegroundColorExpanded, setIsForegroundColorExpanded] =
    useState(false);

  return (
    <Section title="Background">
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          showSwatch={true}
          colors={pickerColors}
          selectedColor={props.backgroundColor}
          onSelectColor={props.onChangeBackgroundColor}
          isExpanded={isBackgroundColorExpanded}
          onToggleExpand={() => setIsBackgroundColorExpanded((prev) => !prev)}
        />
        <Slider
          title="Background opacity"
          min={0}
          max={100}
          value={props.backgroundOpacity * 100}
          onChange={(opacityPerc) => {
            const opacity = opacityPerc / 100;
            props.onChangeBackgroundOpacity(opacity);
          }}
        />
      </div>
      {props.showForegroundControls && (
        <div className={styles.toolboxRowContainer}>
          <ColorPicker
            showSwatch={true}
            colors={pickerColors}
            selectedColor={props.forgroundColor}
            onSelectColor={(color) => props.onChangeForegroundColor?.(color)}
            isExpanded={isForegroundColorExpanded}
            onToggleExpand={() => setIsForegroundColorExpanded((prev) => !prev)}
          />
          <Slider
            title="Foreground opacity"
            min={0}
            max={100}
            value={props.foregroundOpacity * 100}
            onChange={(opacityPerc) => {
              const opacity = opacityPerc / 100;
              props.onChangeForegroundOpacity?.(opacity);
            }}
          />
        </div>
      )}
    </Section>
  );
};

interface BorderControlProps {
  color: string;
  onChangeColor: (color: string) => void;
  width: number;
  onChangeWidth: (width: number) => void;
  cornerRadius: number;
  onChangeCornerRadius: (cornerRadius: number) => void;
  rectShortestSide: number;
}

const BorderControl = (props: BorderControlProps) => {
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Border">
      <div className={styles.toolboxRowContainer}>
        <ColorPicker
          showSwatch={true}
          colors={pickerColors}
          selectedColor={props.color}
          onSelectColor={props.onChangeColor}
          isExpanded={isColorExpanded}
          onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
        />
        <Slider
          title="Width"
          min={0}
          max={30}
          value={props.width}
          onChange={(value) => {
            props.onChangeWidth(value);
          }}
        />
      </div>
      <Slider
        title="Corner radius"
        min={0}
        max={props.rectShortestSide / 2}
        value={props.cornerRadius}
        onChange={(value) => {
          props.onChangeCornerRadius(value);
        }}
      />
    </Section>
  );
};

interface PaddingControlProps {
  padding: number;
  onChangePadding: (padding: number) => void;
}

const PaddingControl = (props: PaddingControlProps) => {
  return (
    <Section title="Padding">
      <Slider
        title="Padding"
        min={0}
        max={100}
        value={props.padding}
        onChange={(value) => {
          props.onChangePadding(value);
        }}
      />
    </Section>
  );
};

interface TextContentControlProps {
  text: string;
  onChangeText: (text: string) => void;
  onClickDeleteLabel: () => void;
}

const TextContentControl = (props: TextContentControlProps) => {
  return (
    <Section>
      <ToolboxComponentInput
        text={props.text}
        onChangeText={props.onChangeText}
        autoFocusOnMount={true}
      />
    </Section>
  );
};

interface BackdropTypeDropdownProps {
  backdrop: Backdrop;
  onChangeType: (type: BackdropType) => void;
}

const BackdropTypeDropdown = (props: BackdropTypeDropdownProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleTypeChange = (type: BackdropType) => {
    setIsVisible(false);
    // Handle the type change logic here
    props.onChangeType(type);
  };

  const label = (
    <div className={styles.typeDropdownLabel}>{props.backdrop.type}</div>
  );
  return (
    <DropdownMenu
      label={label}
      alignment="center"
      isVisible={isVisible}
      onToggleVisibility={() => {
        setIsVisible((prev) => !prev);
      }}
    >
      <div className={styles.typeDropdownContent}>
        <span
          className={styles.typeDropdownItem}
          onClick={() => {
            handleTypeChange("color");
          }}
        >
          COLOR
        </span>
        <span
          className={styles.typeDropdownItem}
          onClick={() => {
            handleTypeChange("image");
          }}
        >
          PHOTO
        </span>
        <span
          className={styles.typeDropdownItem}
          onClick={() => {
            handleTypeChange("dataVisualization");
          }}
        >
          ELEVATION
        </span>
      </div>
    </DropdownMenu>
  );
};

interface ImageControlProps {
  backdrop: Backdrop;
  onChangeImage: (imageName: string) => void;
}

const ImageControl = (props: ImageControlProps) => {
  const { cacheImageForBackdropStyle, isTemplateEditing } = usePosterProject();
  const { resizeAndSaveImage } = useImageStore();
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // `file` is already a Blob
    const blob = file;
    const newFileName = uuid();
    const config: SaveImageConfig = {
      image: blob,
      existingFilename: props.backdrop.style.imageName,
      newFilename: newFileName,
      isPublic: isTemplateEditing,
      folder: "backdrops",
    };
    await resizeAndSaveImage(config, 600, "jpeg");

    const style: BackdropStyle = {
      ...props.backdrop.style,
      imageName: newFileName,
    };
    props.onChangeImage(newFileName);
    cacheImageForBackdropStyle(style);
  };

  return (
    <Section>
      <div className={styles.imageUploadButtonContainer}>
        <input
          id="file-upload"
          className={styles.uploadButton}
          type="file"
          accept=".png, .jpg, .jpeg"
          onChange={handleFileUpload}
        />
        <label htmlFor="file-upload" className={styles.uploadButtonLabel}>
          Upload photo
        </label>
      </div>
    </Section>
  );
};
